import { TrendingUp, Phone, Building, Users, Globe, BadgeDollarSign } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const TaxPlanning = () => {
  return (
    <GenericPracticePage
      title="Tax Planning Services"
      subtitle="Strategic tax planning to minimize your tax liability and maximize savings. Proactive planning saves more money than reactive tax preparation."
      icon={<TrendingUp className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Proactive Tax Planning</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Tax planning is not just filing returns - it's strategic financial planning to legally reduce your tax burden. Whether you're a business owner, high-income individual, or planning your estate, proper tax planning can save thousands or even millions in taxes over time.
            </p>
            <p className="text-lg text-muted-foreground">
              We work with individuals, businesses, and families to develop customized tax strategies that minimize liability while keeping you compliant with federal and state tax laws.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Tax Planning Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Building className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Business Tax Planning</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Strategic planning for business entities to minimize tax burden and maximize profitability.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Entity selection and structuring</li>
                  <li>• Qualified Business Income (QBI) deduction</li>
                  <li>• Depreciation strategies (Section 179, Bonus)</li>
                  <li>• Employee vs. contractor classification</li>
                  <li>• Retirement plan optimization</li>
                  <li>• State and local tax planning</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Users className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Estate Tax Planning</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Minimize estate and gift taxes while preserving wealth for future generations.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Estate tax exemption planning</li>
                  <li>• Gift tax strategies</li>
                  <li>• Trust planning and administration</li>
                  <li>• Charitable giving strategies</li>
                  <li>• Family limited partnerships</li>
                  <li>• Generation-skipping transfer tax</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <Globe className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">International Tax Planning</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Navigate complex international tax issues for individuals and businesses with foreign ties.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• FBAR and FATCA compliance</li>
                  <li>• Foreign earned income exclusion</li>
                  <li>• Foreign tax credits</li>
                  <li>• Expatriation tax planning</li>
                  <li>• Transfer pricing</li>
                  <li>• Tax treaty benefits</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <BadgeDollarSign className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Tax Credits & Incentives</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Identify and maximize available tax credits and incentives for businesses and individuals.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Research & Development (R&D) credits</li>
                  <li>• Energy efficiency credits</li>
                  <li>• Work Opportunity Tax Credit (WOTC)</li>
                  <li>• Employee retention credits</li>
                  <li>• State and local incentives</li>
                  <li>• Opportunity Zone investments</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Year-Round Tax Planning Strategies</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg space-y-4">
              <div>
                <h3 className="font-bold mb-2">Income Timing</h3>
                <p className="text-sm text-muted-foreground">Strategically defer or accelerate income to minimize tax liability across multiple years</p>
              </div>
              <div>
                <h3 className="font-bold mb-2">Deduction Maximization</h3>
                <p className="text-sm text-muted-foreground">Identify all available deductions and time them for maximum benefit</p>
              </div>
              <div>
                <h3 className="font-bold mb-2">Retirement Contributions</h3>
                <p className="text-sm text-muted-foreground">Maximize tax-deferred retirement savings through 401(k)s, IRAs, SEP-IRAs, and defined benefit plans</p>
              </div>
              <div>
                <h3 className="font-bold mb-2">Investment Planning</h3>
                <p className="text-sm text-muted-foreground">Tax-efficient investment strategies including capital gains harvesting and loss harvesting</p>
              </div>
              <div>
                <h3 className="font-bold mb-2">Charitable Contributions</h3>
                <p className="text-sm text-muted-foreground">Donor-advised funds, qualified charitable distributions, and other strategic giving methods</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Business Entity Selection</h2>
            <p className="text-muted-foreground mb-4">Choosing the right business structure has massive tax implications:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">Sole Proprietorship</h3>
                <p className="text-sm text-muted-foreground mb-2"><strong>Pro:</strong> Simple, pass-through taxation</p>
                <p className="text-sm text-muted-foreground"><strong>Con:</strong> Self-employment tax on all profits, unlimited liability</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">LLC (Single or Multi-Member)</h3>
                <p className="text-sm text-muted-foreground mb-2"><strong>Pro:</strong> Flexible taxation, liability protection</p>
                <p className="text-sm text-muted-foreground"><strong>Con:</strong> May pay self-employment tax unless electing S-Corp</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">S-Corporation</h3>
                <p className="text-sm text-muted-foreground mb-2"><strong>Pro:</strong> Avoid self-employment tax on distributions, pass-through</p>
                <p className="text-sm text-muted-foreground"><strong>Con:</strong> Payroll requirements, shareholder restrictions</p>
              </div>
              <div className="bg-card p-4 rounded-lg">
                <h3 className="font-bold mb-2">C-Corporation</h3>
                <p className="text-sm text-muted-foreground mb-2"><strong>Pro:</strong> Lower corporate rates, fringe benefits</p>
                <p className="text-sm text-muted-foreground"><strong>Con:</strong> Double taxation on dividends</p>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Why Plan Year-Round?</h2>
            <p className="text-muted-foreground mb-4">
              Most taxpayers only think about taxes in April. By then, it's too late for strategic planning. Year-round tax planning allows you to:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Make informed business decisions with tax implications in mind</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Time income and deductions for maximum benefit</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Avoid costly mistakes that can't be fixed retroactively</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Prepare for changes in tax law before they take effect</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Estimate and plan for quarterly tax payments</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-accent font-bold">✓</span>
                <span className="text-sm text-muted-foreground">Reduce stress by avoiding last-minute scrambling</span>
              </div>
            </div>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Ready to Reduce Your Tax Burden?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Contact us today for strategic tax planning that saves you money year after year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default TaxPlanning;
